import { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { Button, Menu } from 'antd';
import { useGetCustomNavTitle } from 'hooks/useGetCustomNavTitle';
import { RouteKeyConstants } from 'constants/routeKeyConstants';
import PageHeader from 'components/PageHeader/PageHeader';
import TooltipPopover from 'components/TooltipPopover/TooltipPopover';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import ImageUploader from 'components/ImageUploader/ImageUploader';
import AddOrRemovePlans from 'modules/benefitsWellness/components/AddOrRemovePlans/AddOrRemovePlans';
import PlanSelectButton from 'components/PlanSelectButton/PlanSelectButton';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import {
  FAMILY_AND_LEAVE_CHANNEL,
  OPEN_ADD_NEW_PLAN_MODAL,
} from 'modules/clients/UPClient/UPClientConst';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import { benefitCategory } from 'modules/auth/constants/commonConstants';
import { ReactComponent as FamilyLeave } from 'images/pageHeaderIcons/family-leave.svg';
import FamilyAndLeaveSampleBannerImage from 'images/samples/family.png';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useBenguide from 'hooks/benguide';

import AddPlanApiRequestPayload from 'model/AddPlanApiRequestPayload';
import PlanReference from 'model/PlanReference';
import FamilyAndLeavePlansChannel from 'modules/clients/UPClient/channels/FamilyAndLeaveChannel';
import FamilyAndLeaveCard from 'modules/familyAndLeave/components/FamilyAndLeaveCard/FamilyAndLeaveCard';

import { getSectionImageUrl } from 'services/BenGuideService';
import {
  fetchFamilyAndLeavePlanList,
  handleSectionImageUpload,
  addMultiplePlans,
  getDefaultText,
} from 'modules/familyAndLeave/slices/familyAndLeaveSlice';
import { swapElements } from 'modules/benefitsWellness/util';
import {
  BENEFIT_CATEGORY,
  FAMILY_AND_LEAVE_BENEFITS_KIND,
  SPANISH_MODE_PLAN_MANAGEMENT_TOOLTIP,
} from 'constants/commonConstants';
import { SectionName } from 'modules/home/constants';
import editIcon from 'images/icon-edit.svg';
import BenefitGuideRefreshChannel from 'modules/clients/UPClient/channels/BenefitGuideRefreshChannel';
import BenefitGuideNotificationsChannel from 'modules/clients/UPClient/channels/BenefitGuideNotificationsChannel';
import { NotificationContextProvider } from 'context/NotificationContext';
import NoPlansView from 'components/NoPlansView/NoMDVPlansView';
import OriginalImageCropParams from 'model/OriginalImageCropParams';
import { getBase64Image } from 'utils/screenshotUtil';
import { benguideApiV2 } from 'utils/apiUtil';
import { formatDataId } from 'utils/commonUtil';
import PageSection from 'model/PageSection';
import FooterTextSection from 'components/FooterTextSection';

import styles from './familyAndLeave.module.less';

const FamilyAndLeave: FC = () => {
  const upClient = useUPClient();
  const dispatch = useAppDispatch();
  const benguide = useBenguide();
  const {
    planList: planListWrapper,
    familyAndLeaveSection,
    sections = [],
  } = useAppSelector((state) => state.familyAndLeave);
  const [sortedFamilyPlans, setSortedPlans] = useState<any[]>([]);
  const [bannerImage, setBannerImage] = useState<string>('');
  const [isSaveDisable, setSaveDisable] = useState<boolean>(false);
  const [originalImg, setOriginalImg] = useState<string>('');
  const imageUploaderRef = useRef(null);
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);
  const addedPlansLoading = useAppSelector(
    (state) => state.familyAndLeave.familyAndLeaveSection.inProgress
  );
  const language = useAppSelector((state) => state.benguide.data?.language);
  const {
    employerId,
    planYearId,
    masterId,
    isEditMode,
    benefitClasses,
    revision,
    latestRevision,
  } = benguide;
  const { data: planList = [], inProgress } = planListWrapper;
  const { plans: addedPlans = [] } = familyAndLeaveSection;

  const customNavigationTitles = useGetCustomNavTitle({
    key: RouteKeyConstants.FAMILY_LEAVE as keyof typeof RouteKeyConstants,
  });

  const listFamilyAndLeavePlans = useCallback(() => {
    dispatch(
      fetchFamilyAndLeavePlanList(employerId, planYearId, benefitClasses)
    );
  }, [benefitClasses, dispatch, employerId, planYearId]);

  useEffect(() => {
    listFamilyAndLeavePlans();
  }, [listFamilyAndLeavePlans]);

  useEffect(() => {
    if (masterId) {
      setBannerImage(
        getSectionImageUrl(
          masterId,
          SectionName.FAMILY_AND_LEAVE_IMAGE,
          latestRevision || revision
        )
      );
    }
  }, [dispatch, masterId, revision, latestRevision]);

  useEffect(() => {
    if (addedPlans) {
      const uniquePlansList = uniqBy(addedPlans, 'benefitKind');
      if (uniquePlansList) {
        uniquePlansList.forEach((element: any) => {
          if (element.id && element.benefitKind) {
            dispatch(getDefaultText(masterId, element.id, element.benefitKind));
          }
        });
      }
      setSortedPlans(addedPlans);
    }
  }, [dispatch, addedPlans, masterId]);

  const moveTop = (index: number) => {
    if (addedPlansLoading) return;
    if (index > 0) {
      const newPlans = swapElements(
        sortedFamilyPlans,
        index,
        index - 1
      ) as any[];
      const updatedPlansWithOrder = newPlans.map((plan, i) => ({
        ...plan,
        orderNo: i + 1,
      }));
      setSortedPlans(updatedPlansWithOrder);
      updatePlans(updatedPlansWithOrder, false);
    }
  };

  const moveBottom = (index: number) => {
    if (addedPlansLoading) return;
    if (index < sortedFamilyPlans.length - 1) {
      const newPlans = swapElements(
        sortedFamilyPlans,
        index,
        index + 1
      ) as any[];
      const updatedPlansWithOrder = newPlans.map((plan, i) => ({
        ...plan,
        orderNo: i + 1,
      }));
      setSortedPlans(updatedPlansWithOrder);
      updatePlans(updatedPlansWithOrder, false);
    }
  };

  const updatePlans = (plans: any[], orderByPlanNames: boolean) => {
    const planReferences = plans.map((plan: PlanReference) => {
      return {
        ...plan,
        planId: plan.id,
        planName: plan.name,
        carrier: {
          id: get(plan, 'carrier.id', null),
          name: get(plan, 'carrier.name', null),
        },
        benefitCarrier: {
          id: get(plan, 'benefitCarrier.id', null),
          name: get(plan, 'benefitCarrier.name', null),
        },
      };
    });

    const addedPlansPayload: Array<AddPlanApiRequestPayload> = [];
    FAMILY_AND_LEAVE_BENEFITS_KIND.forEach((benefitKind: string) => {
      return addedPlansPayload.push({
        sectionName: benefitKind,
        plans: planReferences.filter(
          (planRef) =>
            planRef.benefitKind && planRef.benefitKind === benefitKind
        ),
        orderByPlanNames: orderByPlanNames,
      });
    });
    dispatch(addMultiplePlans(masterId, addedPlansPayload));
  };

  const getPlanTypes = () => {
    return (
      <Menu>
        {[
          benefitCategory.PARENTAL_LEAVE,
          benefitCategory.BACKUP_CHILDCARE,
          benefitCategory.FERTILITY,
          benefitCategory.BEREAVEMENT,
          benefitCategory.FAMILY_AND_LEAVE_OTHER,
        ].map((item, key) => {
          return (
            <Menu.Item
              key={key}
              className={styles.menuItemHeader}
              data-id={formatDataId(`${item.label}-optn`)}
            >
              <EditableHotspot
                alwaysVisible={false}
                useChannel={{
                  client: upClient,
                  channel: FAMILY_AND_LEAVE_CHANNEL,
                  event: OPEN_ADD_NEW_PLAN_MODAL,
                  data: { item },
                }}
              >
                {item.label}
              </EditableHotspot>
            </Menu.Item>
          );
        })}
      </Menu>
    );
  };

  const handleImageUpload = (
    image: string,
    originalImage?: string,
    cropArea?: OriginalImageCropParams
  ) => {
    setLoadingImage(true);
    setBannerImage(image);
    dispatch(
      handleSectionImageUpload(
        masterId,
        image,
        SectionName.FAMILY_AND_LEAVE_IMAGE,
        originalImage,
        cropArea
      )
    );
  };

  useEffect(() => {
    if (sections) {
      const imgSection = sections.find(
        (section: PageSection) =>
          section.name === SectionName.FAMILY_AND_LEAVE_IMAGE
      );
      if (imgSection?.data?.originalImageReference) {
        getBase64Image(
          `${benguideApiV2}/${masterId}/images?section-name=${
            SectionName.FAMILY_AND_LEAVE_IMAGE
          }&original=true&revision=${latestRevision || revision}`
        ).then((data: any) => {
          setOriginalImg(data);
          if (loadingImage) {
            (imageUploaderRef?.current as any)?.close();
            setLoadingImage(false);
          }
        });
      }
    }
    // No need call when change loadingImage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterId, sections, latestRevision]);

  const sectionImageCrop = useMemo(() => {
    if (sections) {
      const imgSection = sections?.find(
        (section: PageSection) =>
          section.name === SectionName.FAMILY_AND_LEAVE_IMAGE
      );

      return imgSection
        ? (imgSection as PageSection).data.originalImageCropParams
        : null;
    }
    return null;
  }, [sections]);

  const showAlert = () => {
    setIsAlertVisible(true);
  };

  const getAlert = () => (
    <AlertMessage
      className={styles.alert}
      type="success"
      message={'Successfully created new plan.'}
      closeAlert={() => setIsAlertVisible(false)}
      wrapperClassName={styles.planAlertWrapper}
    />
  );

  return (
    <NotificationContextProvider isDisableSave={isSaveDisable}>
      {isAlertVisible && getAlert()}
      <div className={styles.familyAndLeaveWrapper}>
        <FamilyAndLeavePlansChannel />
        <BenefitGuideRefreshChannel
          benefitKind={BENEFIT_CATEGORY.FAMILY_AND_LEAVE.value}
        />
        <BenefitGuideNotificationsChannel
          setDisableSave={() => setSaveDisable(true)}
        />
        <PageHeader
          pageHeaderTitle={customNavigationTitles}
          pageHeaderIcon={<FamilyLeave className={styles.famIcon} />}
          managePlansButton={
            <>
              {isEditMode &&
                !isEmpty(addedPlans) &&
                (language === 'SPANISH' ? (
                  <TooltipPopover
                    content={SPANISH_MODE_PLAN_MANAGEMENT_TOOLTIP}
                    placement="right"
                  >
                    <Button disabled>+ Add or Remove Plans</Button>
                  </TooltipPopover>
                ) : (
                  <EditableHotspot
                    alwaysVisible={true}
                    customModal={(visible: boolean, onClose: Function) => (
                      <AddOrRemovePlans
                        onClose={onClose}
                        visible={visible}
                        isLoading={inProgress}
                        title="Family & Leave"
                        onSave={updatePlans}
                        addedPlanList={addedPlans}
                        allPlans={planList || []}
                        reloadPlans={listFamilyAndLeavePlans}
                        channel={FAMILY_AND_LEAVE_CHANNEL}
                        actionContent={
                          language === 'SPANISH' ? (
                            <PlanSelectButton
                              overlay={getPlanTypes()}
                              selectLabel="Create New Plan"
                              disabled={true}
                            />
                          ) : (
                            <PlanSelectButton
                              overlay={getPlanTypes()}
                              selectLabel="Create New Plan"
                            />
                          )
                        }
                        showAlert={showAlert}
                      />
                    )}
                  >
                    <Button>+ Add or Remove Plans</Button>
                  </EditableHotspot>
                ))}
            </>
          }
        />

        <EditableHotspot
          alwaysVisible={false}
          editText="Edit"
          editIcon={<img src={editIcon} alt="edit" />}
          customModal={(visible: boolean, onClose: Function) => (
            <ImageUploader
              title="Family & Leave Photo"
              cropShape="rect"
              uploadedImage={bannerImage}
              aspect={1}
              loading={false}
              onConfirm={handleImageUpload}
              showOperations
              uploadNewImgText="+ Upload New Photo"
              isOpenImageTray={visible}
              onCloseImgTray={onClose}
              uploadImgAfterResetText="Upload Photo"
              hideUploader
              customCropper={{ width: 780, height: 248 }}
              defaultLogo={FamilyAndLeaveSampleBannerImage}
              imagePreviewStyle={styles.mainImage}
              uploadWrapperClass={styles.imageUploadWrapper}
              maxSizeMB={4}
              defaultCropArea={sectionImageCrop}
              unCroppedImage={originalImg}
              ref={imageUploaderRef}
              preventCloseOnConfirm={true}
              saving={loadingImage}
            />
          )}
        >
          <></>
        </EditableHotspot>
        {isEmpty(addedPlans) && isEditMode && (
          <AddOrRemovePlans
            visible={true}
            withoutModal={true}
            isLoading={inProgress}
            title="Family & Leave"
            onSave={updatePlans}
            addedPlanList={addedPlans}
            allPlans={planList || []}
            reloadPlans={listFamilyAndLeavePlans}
            channel={FAMILY_AND_LEAVE_CHANNEL}
            actionContent={
              language === 'SPANISH' ? (
                <PlanSelectButton
                  overlay={getPlanTypes()}
                  selectLabel="Create New Plan"
                  className={styles.darkButtonDisabled}
                  disabled={true}
                />
              ) : (
                <PlanSelectButton
                  overlay={getPlanTypes()}
                  selectLabel="Create New Plan"
                  className={isEmpty(planList) ? styles.darkButton : ''}
                />
              )
            }
            showAlert={showAlert}
          />
        )}

        <div className={styles.planCardsContainer}>
          {sortedFamilyPlans?.map((plan, index) => (
            <FamilyAndLeaveCard
              key={index}
              plan={plan}
              moveTop={() => moveTop(index)}
              moveBottom={() => moveBottom(index)}
              hideTop={index === 0}
              hideBottom={index === sortedFamilyPlans?.length - 1}
              renderReorder={sortedFamilyPlans?.length > 1}
            />
          ))}
          {isEmpty(sortedFamilyPlans) && !isEditMode && (
            <NoPlansView className={styles.noPlanView} />
          )}
        </div>
        {!isEmpty(sortedFamilyPlans) && (
          <FooterTextSection masterId={masterId} isEditMode={isEditMode} />
        )}
      </div>
    </NotificationContextProvider>
  );
};

export default FamilyAndLeave;
